import type { FC } from 'react'

import { useExpandableText } from '@skytech/utils'
import { mapInternalLocaleToDayjs } from '@skytech/utils/translatedLocale'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import CheckIcon from 'mdi-react/CheckIcon'
import { InView } from 'react-intersection-observer'

import { useAppContext } from '@/App.context'
import { Translation } from '@/components/i18n/Translation'
import { getFromLocalStorage, saveToLocalStorage } from '@/util/persistance'

import type { SystemMessage as SystemMessageType } from './types'

dayjs.extend(relativeTime)

const SystemMessage: FC<
  SystemMessageType & {
    seenMessages?: number[]
    maxTextHeight?: number
    onSeenMessagesSet: (v: number[]) => void
  }
> = ({ id, start, message, header, seenMessages, maxTextHeight = 200, onSeenMessagesSet }) => {
  const { locale } = useAppContext()
  const { textRef, isTextExpandable } = useExpandableText(maxTextHeight)

  const dayjsLocale = mapInternalLocaleToDayjs(locale)
  const startedAgo = dayjs().locale(dayjsLocale).to(dayjs(start))
  const isRead = seenMessages?.includes(id)

  const handleMessageSeen = (isVisible: boolean) => {
    const readMessages = getFromLocalStorage('read-system-messages')

    if (!isVisible) return

    if (!readMessages) {
      saveToLocalStorage('read-system-messages', [id])
      onSeenMessagesSet([id])

      return
    }

    if (isRead) return

    saveToLocalStorage('read-system-messages', [...readMessages, id])
    onSeenMessagesSet([...readMessages, id])
  }

  return (
    <div>
      <div className="message-header">
        <small>{startedAgo}</small>

        {isRead && (
          <small className="read-info">
            <span role="img" aria-label="checkmark-icon" className="read-icon">
              <CheckIcon />
            </span>

            <Translation id="read" />
          </small>
        )}
      </div>

      <h3>{header}</h3>

      <InView threshold={1} onChange={handleMessageSeen}>
        <p ref={textRef} className={`message ${isTextExpandable ? 'is-message-expandable' : ''}`}>
          {message}
        </p>
      </InView>
    </div>
  )
}

export default SystemMessage
